import _ from 'lodash';
import {OLD_NORMALIZATION_STATE_TASK_PROPERTIES} from '../constants/oldStateEnum';
import {CardType, NormalizationType} from '../constants/dataEnum';
import {cloneAndMapObject, find} from './objectHelper';
// eslint-disable-next-line import/no-cycle
import {convertCardToSimpleTransferAndRearrange} from './cardAndTaskHelper';
import {CsvCellKeysEnum} from './protocolConversionHelper';

/**
 * This helper is used to convert the current/upcoming state and/or feature into old format (SW version 1.27 and below)
 * in the case of user downgrading their software, so that the protocol can still be opened.
 *
 * Should be maintained if there's any format changes in cardState, labwareState, or any of its children properties.
 */

const joinMixingPositionAspirateAndDispense = (task) => {
  let newTask = _.cloneDeep(task);
  newTask = {...newTask, mixingPosition: task?.mixingPositionAspirate};

  delete newTask[CsvCellKeysEnum.MIXING_POSITION_ASPIRATE];
  delete newTask[CsvCellKeysEnum.MIXING_POSITION_DISPENSE];

  return task;
};

/**
 * Used to map current normalization card format into old format.
 * If the changes is unsupported the card will become a simple transfer card.
 * @param {Card} normalizationCard
 * @param {Object} labwareState
 * @returns old version of normalization card
 */
const downgradeNormalizationCardVersion = (normalizationCard, labwareState, cardState) => {
  const {normalizationState} = normalizationCard;
  const {diluentLabwareID, sampleLabwareID, diluentWell, type} = normalizationState;

  const isUnsupported = type === NormalizationType.FIXED_DILUENT_VOLUME;

  if (isUnsupported) {
    return {
      isUnsupported,
      data: convertCardToSimpleTransferAndRearrange(normalizationCard, cardState),
    };
  }

  const diluentLabware = find(labwareState, (labware) => labware.LabwareID === diluentLabwareID);
  const sampleLabware = find(labwareState, (labware) => labware.LabwareID === sampleLabwareID);

  // 1.28.0.0 normalization handler
  const newNormalizationState = cloneAndMapObject(
    {
      ...normalizationState,
      selectedLabware: sampleLabware,
      selectedDiluentWell: {...diluentWell, labware: diluentLabware},
    },
    OLD_NORMALIZATION_STATE_TASK_PROPERTIES,
  );

  return {
    isUnsupported,
    data: {...normalizationCard, normalizationState: newNormalizationState},
  };
};

/**
 * Used to create state content for older software version that still uses stateContent to open protocol.
 * @param {Object} cardState
 * @param {Object} labwareState
 * @returns stateContent object.
 */
export const getStateContentForOlderVersion = (cardState, labwareState, currentSWVersion) => {
  const {cards, groups, tasks} = cardState;

  const updatedTasks = {...tasks};
  let updatedCards = {...cards};
  let updatedGroups = {...groups};

  Object.values(cards).forEach((card) => {
    if (card.type === CardType.NORMALIZATION) {
      const updatedCardState = {...cardState, cards: updatedCards, groups: updatedGroups};
      const {isUnsupported, data} = downgradeNormalizationCardVersion(card, labwareState, updatedCardState);

      if (isUnsupported) {
        updatedGroups = data.groups;
        updatedCards = data.cards;
      } else {
        updatedCards[card.ID] = data;
      }
    }
  });

  Object.values(tasks).forEach((task) => {
    tasks[task.ID] = joinMixingPositionAspirateAndDispense(task);
  });

  return JSON.stringify({
    cardState: {...cardState, cards: updatedCards, tasks: updatedTasks},
    labwareState,
    softwareVersion: currentSWVersion,
  });
};

export default getStateContentForOlderVersion;
